import React, { useEffect, useState } from 'react';
import { Chip, Link, Sheet, Stack, Typography } from '@mui/joy';
import { getSyncAuditLog } from '../../integrations/freeagentSync';
import type { Document } from '../../modules/docs/types/documents.types';

type FreeAgentEvent = {
  id: string;
  invoiceRef: string;
  action: string;
  createdAt: string;
  freeagentUrl?: string;
  errorMessage?: string;
  metadata?: { action?: string; newStatus?: string } | null;
};

/**
 * Transmissions only: the invoice being issued to FreeAgent, or failing to be.
 * Later status updates (marked paid/sent/cancelled) belong to Recent activity.
 */
export function freeAgentEventLabel(event: Pick<FreeAgentEvent, 'action' | 'metadata'>): string | null {
  if (event.action === 'error') {
    const status = event.metadata?.newStatus;
    return status === 'paid' || status === 'rejected' ? null : 'Failed';
  }
  const step = event.metadata?.action;
  if (step === 'create_and_send' || step === 'create') return 'Issued to FreeAgent';
  return null;
}

export default function FreeAgentTransmissionsList({
  invoices,
  refreshKey,
}: {
  invoices: Document[];
  refreshKey?: string | number;
}) {
  const [events, setEvents] = useState<FreeAgentEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const invoiceKey = invoices.map((invoice) => `${invoice.id}:${invoice.updatedAt || ''}`).join('|');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(invoices.map(async (invoice) => {
      const rows = await getSyncAuditLog(invoice.id);
      const invoiceRef = invoice.invoiceRef || invoice.lifecycleRef || 'Invoice';
      return rows.map((row) => ({ ...row, invoiceRef }));
    }))
      .then((lists) => {
        if (cancelled) return;
        const next = lists.flat()
          .filter((row) => freeAgentEventLabel(row))
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        setEvents(next);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceKey, refreshKey]);

  return (
    <Stack gap={1}>
      <Stack gap={0.25}>
        <Typography level="title-sm">FreeAgent</Typography>
        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
          Invoices issued to FreeAgent.
        </Typography>
      </Stack>
      {loading && events.length === 0 ? (
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Loading…</Typography>
      ) : events.length === 0 ? (
        <Sheet variant="soft" sx={{ p: 1.25, borderRadius: 'md' }}>
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>Nothing sent to FreeAgent yet.</Typography>
        </Sheet>
      ) : (
        <Stack spacing={1.1}>
          {events.map((event) => {
            const failed = event.action === 'error';
            return (
              <Sheet
                key={event.id}
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 'md',
                  bgcolor: 'background.body',
                  borderColor: failed ? 'danger.200' : 'neutral.300',
                }}
              >
                <Stack spacing={0.65}>
                  <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip size="sm" variant="soft" color="neutral">
                      {new Date(event.createdAt).toLocaleString('en-GB')}
                    </Chip>
                    {invoices.length > 1 && (
                      <Chip size="sm" variant="soft" color="primary">{event.invoiceRef}</Chip>
                    )}
                    <Chip size="sm" variant={failed ? 'solid' : 'soft'} color={failed ? 'danger' : 'success'}>
                      {freeAgentEventLabel(event)}
                    </Chip>
                  </Stack>
                  {failed && event.errorMessage && (
                    <Typography level="body-xs" sx={{ color: 'danger.plainColor' }}>{event.errorMessage}</Typography>
                  )}
                  {event.freeagentUrl && (
                    <Link
                      href={event.freeagentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      level="body-sm"
                      sx={{ fontWeight: 600 }}
                    >
                      Open in FreeAgent
                    </Link>
                  )}
                </Stack>
              </Sheet>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
